import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CsvPreviewData } from '../../core/models';
import { TimestampToDatePipe } from '../../core/timestamp-to-date.pipe';

@Component({
  selector: 'app-csv-data-table',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TimestampToDatePipe,
  ],
  standalone: true,
  template: ` <div class="px-4 py-4">
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">Imported Bookings</h1>
        <p class="mt-2 text-sm text-gray-700">
          Preview and select which bookings to import. Invalid rows are
          highlighted.
        </p>
      </div>
    </div>
    <div class="mt-8 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div
            class="overflow-hidden shadow-sm ring-1 ring-black/5 sm:rounded-lg"
          >
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr>
                  <th class="py-3.5 pr-3 pl-4 sm:pl-6">
                    <input
                      type="checkbox"
                      [checked]="allSelected()"
                      (change)="toggleAllSelections()"
                      class="h-4 w-4 text-columbus-blue-600 border-gray-300 rounded"
                    />
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Nr.
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Start
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    End
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Tour Code
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Ship
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Status
                  </th>
                  <th scope="col" class="relative py-3.5 pr-4 pl-3 sm:pr-6">
                    <span class="sr-only">View</span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr
                  *ngFor="let booking of bookings; let i = index"
                  [class.bg-red-50]="!booking.isValid"
                >
                  <td class="py-4 pr-3 pl-4 sm:pl-6">
                    <input
                      type="checkbox"
                      [disabled]="!booking.isValid"
                      [(ngModel)]="booking.selected"
                      class="h-4 w-4 disabled:opacity-25 text-columbus-blue-600 border-gray-300 rounded"
                    />
                  </td>
                  <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-900">
                    {{ i + 1 }}
                  </td>
                  <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-900">
                    {{
                      booking.duration.date
                        | timestampToDate
                        | date: 'dd.MM.YYYY'
                    }}
                  </td>
                  <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-900">
                    {{
                      booking.duration.startTime
                        | timestampToDate
                        | date: 'HH:mm' : 'UTC'
                    }}
                  </td>
                  <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-900">
                    {{
                      booking.duration.endTime
                        | timestampToDate
                        | date: 'HH:mm' : 'UTC'
                    }}
                  </td>
                  <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-900">
                    {{ booking.duration.tourCode }}
                  </td>
                  <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-900">
                    {{ booking.ship.shipName }}
                  </td>
                  <td class="px-3 py-4 text-sm whitespace-nowrap">
                    <span
                      *ngIf="booking.isValid"
                      class="text-green-600 font-medium"
                      >✅ Valid</span
                    >
                    <span
                      *ngIf="!booking.isValid"
                      class="text-red-600 font-medium"
                      >❌ Invalid</span
                    >
                  </td>
                  <td
                    class="relative py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-6"
                  >
                    <button
                      (click)="onSelectBooking(booking)"
                      class="text-columbus-blue-600 hover:text-columbus-blue-900"
                    >
                      View<span class="sr-only">, Details</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              *ngIf="bookings.length === 0"
              class="text-center py-4 text-gray-400"
            >
              No bookings to preview.
            </div>
          </div>
        </div>
      </div>
      <div class="py-4 w-full flex justify-end">
        <button
          (click)="triggerImport()"
          class="disabled:opacity-25 px-4 py-2 bg-columbus-blue-600 text-white rounded hover:bg-columbus-blue-700"
          [disabled]="!checkIfAnySelected()"
        >
          Import Bookings
        </button>
      </div>
    </div>
  </div>`,
  styles: ``,
})
export class CsvDataTableComponent {
  @Input() bookings: CsvPreviewData[] = [];
  @Output() selectBooking = new EventEmitter<CsvPreviewData>();
  @Output() importBookings = new EventEmitter<CsvPreviewData[]>();

  onSelectBooking(booking: CsvPreviewData): void {
    this.selectBooking.emit(booking);
  }

  toggleAllSelections(): void {
    const allSelected = this.allSelected();
    this.bookings.forEach((b) => (b.selected = !allSelected));
  }

  allSelected(): boolean {
    return (
      this.bookings.length > 0 &&
      this.bookings.every((b) => {
        if (b.isValid) {
          return b.selected;
        } else {
          return (b.selected = false);
        }
      })
    );
  }

  triggerImport() {
    const importBookings = this.bookings.filter((b) => b.selected);
    this.importBookings.emit(importBookings);
  }

  checkIfAnySelected() {
    return this.bookings.some((b) => b.selected);
  }
}
